import type { ReactNode } from 'react';
import React, { useRef } from 'react';

import { CLIVE_AVATARS } from '~/components/Clive/cliveRegistry';
import type { CliveRecommendationAvatarType } from '~/components/Clive/types';
import { Caption, Heading, MainCard } from '~/components/core';
import cn from '~/Utils/cn';

interface CliveRecommendationPanelProps {
  subtitle?: string;
  title: string;
  actionComponent?: ReactNode;
  content?: ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  openByDefault?: boolean;
  avatarType?: CliveRecommendationAvatarType;
  onExpand?: () => void;
}

const AVATAR_SIZE = 40;

const CliveRecommendationPanel: React.FC<React.PropsWithChildren<CliveRecommendationPanelProps>> = ({
  title,
  openByDefault = false,
  children,
  actionComponent,
  content,
  subtitle,
  avatarType = 'happy',
  onExpand,
}) => {
  const isCollapsible = Boolean(children);
  const AvatarComponent = CLIVE_AVATARS[avatarType];

  // Track if we've already fired the expand event for this session
  const hasExpandedRef = useRef(openByDefault || false);

  const handleToggle = () => {
    // Only fire onExpand once - the first time the panel is expanded
    if (!hasExpandedRef.current && onExpand) {
      hasExpandedRef.current = true;
      onExpand();
    }
  };

  return (
    <MainCard
      direction="right"
      type="contained"
      title={
        <CliveRecommendationPanelTitle
          title={title}
          subtitle={subtitle}
          actionComponent={actionComponent}
          content={content}
        />
      }
      collapsible={isCollapsible}
      openByDefault={openByDefault}
      onCollapseClick={handleToggle}
      titleAction={AvatarComponent ? <AvatarComponent size={AVATAR_SIZE} /> : null}
      titleClassName="min-w-0"
      // The title block grows with the subtitle, timeline and action button, so the avatar and
      // chevron sit at the top of it rather than floating in the middle of a tall card.
      titleRowClassName="items-start"
      className={cn('rounded-xl border-0 bg-azure-400 shadow-none', {
        'pb-0': !isCollapsible,
      })}
    >
      {children}
    </MainCard>
  );
};

const CliveRecommendationPanelTitle: React.FC<{
  title: string;
  subtitle?: string;
  actionComponent?: ReactNode;
  content?: ReactNode;
}> = ({ title, subtitle, actionComponent, content }) => {
  return (
    // Only the heading shares a row with the action button. The subtitle and content sit beneath it so
    // they run the width of the block instead of being squeezed into the gap next to the button.
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-16">
        <Heading variant={subtitle ? 'h4' : 'h2'} className="min-w-0 flex-1">
          {title}
        </Heading>
        {actionComponent ? <div className="shrink-0">{actionComponent}</div> : null}
      </div>
      {subtitle ? <Caption className="mt-8">{subtitle}</Caption> : null}
      {content ? <div className="mt-8">{content}</div> : null}
    </div>
  );
};

export default CliveRecommendationPanel;
